(() => {
  if (customElements.get('aos-gift-card-balance')) return
  const states = {
    active: ['Active', ''],
    expired: ['Expired', 'This gift card has expired. The balance shown is no longer available to spend.'],
    fully_redeemed: ['Fully used', 'This gift card has no remaining balance.'],
    voided: ['No longer valid', 'This gift card is no longer valid. Please contact our team if you need help.'],
    not_activated: ['Not yet activated', 'This gift card has not been activated yet. Please contact our team if you have already purchased it.'],
  }
  class GiftCardBalance extends HTMLElement {
    connectedCallback() {
      if (this.ready) return
      this.ready = true
      this.form = this.querySelector('form')
      this.input = this.querySelector('input')
      this.button = this.querySelector('button')
      this.message = this.querySelector('.aos-gc__message')
      this.result = this.querySelector('.aos-gc__result')
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.lookup() })
      this.input.addEventListener('input', () => this.reset())
    }
    disconnectedCallback() { this.controller?.abort() }
    reset() {
      this.controller?.abort()
      this.controller = null
      this.result.hidden = true
      this.message.hidden = true
      this.button.disabled = false
      this.button.textContent = 'Check balance'
      this.input.removeAttribute('aria-invalid')
    }
    async lookup() {
      this.reset()
      const number = this.input.value.trim()
      if (!number) { this.input.focus(); return }
      const controller = new AbortController()
      this.controller = controller
      this.button.disabled = true
      this.button.textContent = 'Checking…'
      this.message.hidden = false
      this.message.removeAttribute('data-error')
      this.message.textContent = 'Checking your gift card…'
      const timeout = setTimeout(() => controller.abort(), 12000)
      try {
        const endpoint = new URL(this.dataset.endpoint)
        const local = ['localhost', '127.0.0.1'].includes(endpoint.hostname)
        if (endpoint.protocol !== 'https:' && !(local && endpoint.protocol === 'http:')) throw Error()
        const response = await fetch(endpoint, {
          method: 'POST', credentials: 'omit', cache: 'no-store', signal: controller.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ shop: this.dataset.shop, cardNumber: number }),
        })
        const data = await response.json()
        if (this.controller !== controller) return
        if (!response.ok) {
          const messages = {
            400: 'Please enter the card number printed on your gift card.',
            404: 'We could not find that gift card. Check the card number and try again.',
            429: 'Too many attempts. Please wait a minute and try again.',
          }
          const problem = Error('Lookup failed')
          problem.publicMessage = messages[response.status] || 'Balance checking is temporarily unavailable. Please try again shortly.'
          throw problem
        }
        const card = data.card
        if (!card || !Object.hasOwn(states, card.status)) throw Error()
        this.querySelector('.aos-gc__state').textContent = states[card.status][0]
        this.querySelector('.aos-gc__status-note').textContent = states[card.status][1]
        const activated = card.status !== 'not_activated'
        this.querySelector('.aos-gc__amount-wrap').hidden = !activated
        this.querySelector('.aos-gc__expiry-wrap').hidden = !activated
        if (activated) {
          if (card.balance === null || !Number.isFinite(Number(card.balance)) || !/^[A-Z]{3}$/.test(card.currency)) throw Error()
          if (card.redeemedAmount === null || !Number.isFinite(Number(card.redeemedAmount))) throw Error()
          this.querySelector('.aos-gc__balance-label').textContent = card.status === 'expired' ? 'Recorded balance' : 'Remaining balance'
          this.querySelector('.aos-gc__amount').textContent = new Intl.NumberFormat('en-GB', { style:'currency', currency:card.currency }).format(Number(card.balance))
          this.querySelector('.aos-gc__used').textContent = new Intl.NumberFormat('en-GB', { style:'currency', currency:card.currency }).format(Number(card.redeemedAmount))
          if (card.expiresAt && !/^\d{4}-\d{2}-\d{2}$/.test(card.expiresAt)) throw Error()
          this.querySelector('.aos-gc__expiry').textContent = card.expiresAt
            ? new Intl.DateTimeFormat('en-GB', { day:'numeric', month:'long', year:'numeric', timeZone:'UTC' }).format(new Date(`${card.expiresAt}T12:00:00Z`))
            : 'No expiry'
        }
        this.message.hidden = true
        this.result.hidden = false
      } catch (error) {
        if (this.controller !== controller) return
        this.message.hidden = false
        this.message.setAttribute('data-error', '')
        this.message.textContent = error.name === 'AbortError' ? 'That took longer than expected. Please try again.'
          : error.publicMessage || 'We cannot check your balance right now. Please try again shortly.'
      } finally {
        clearTimeout(timeout)
        if (this.controller === controller) {
          this.button.disabled = false
          this.button.textContent = 'Check balance'
        }
      }
    }
  }
  customElements.define('aos-gift-card-balance', GiftCardBalance)
})()
